import { defineStore } from 'pinia'
import { ref } from 'vue'

import { orderService } from '@/api/services/order'
import { ORDER_STATUS, type ApiOrderListItem } from '@/api/types/order'
import { useLocalStorage } from '@/composables/useLocalStorage'
import { ORDER_TYPE } from '@/composables/useDelivery'
import { useRequest } from '@/composables/useRequest'
import { useUserStore } from '@/stores/user'
import { isActiveOrder, orderNumber, statusStep } from '@/utils/order'

/** Как часто спрашиваем список заказов, пока хоть один в работе. */
const POLL = 45_000

/**
 * Сколько карточка остаётся уместной. В списке лежит вся история заказов,
 * и без этого окна клиент на свежей установке получал поздравление с
 * доставкой недельной давности: отметка «уже рассказали» живёт только
 * в localStorage, а там у него пусто.
 */
const FRESH_MS = 6 * 60 * 60 * 1000

/**
 * Свежесть считаем по обещанному времени, а не по дате оформления: у
 * предзаказа оно на несколько дней вперёд, и до самой доставки такой заказ
 * остаётся актуальным — время в будущем тоже считается свежим.
 */
function momentOf(item: ApiOrderListItem): number {
  const moment = item.plannedDateTime || item.order.dateTime
  return moment ? new Date(moment).getTime() : 0
}

function isFresh(item: ApiOrderListItem): boolean {
  const moment = momentOf(item)
  return moment > 0 && Date.now() - moment < FRESH_MS
}

/** Что показывает карточка: сам заказ и шаг, до которого он дошёл. */
export interface OrderNotice {
  orderId: string
  number: string
  step: number
  isPickup: boolean
  /** Минут до готовности; 0 — время прошло или его не обещали */
  eta: number
}

/**
 * Карточки статуса заказа. Пуш-уведомлений у мини-аппа нет, поэтому о том,
 * где заказ, клиент узнаёт из приложения: опрашиваем список заказов и
 * показываем карточку на главной.
 *
 * Карточка приходит один раз за запуск мини-аппа: встретила на входе — и всё,
 * дальше клиент ходит по приложению спокойно, а за статусом идёт в сам заказ.
 * Следующий раз она поздоровается уже при следующем открытии.
 *
 * Про завершённый заказ («доставлен», «можно забирать») рассказываем ровно
 * один раз за всё время, поэтому такая отметка переживает перезапуск.
 *
 * Опрос идёт, только пока есть заказ в работе и карточку ещё не показали:
 * доставленный и отменённый больше не меняются, а после показа ждать нечего.
 */
export const useOrderNoticeStore = defineStore('orderNotice', () => {
  const userStore = useUserStore()

  /* Тихий: клиент этот запрос не звал, и тост об ошибке его бы только смутил */
  const request = useRequest(orderService.list, { silent: true })

  /**
   * До какого шага рассказали о завершённом заказе. Переживает перезапуск:
   * «Заказ доставлен» должен поздравить один раз, а не при каждом входе.
   */
  const seen = useLocalStorage<Record<string, number>>('order-notice', {})

  /**
   * Показали ли карточку в этот запуск. Живёт, пока жива страница: клиент
   * ходит между главной, меню и корзиной сколько угодно — второй раз она не
   * всплывёт, даже если за это время заказ шагнул дальше.
   */
  let didShow = false

  const notice = ref<OrderNotice | null>(null)

  function markSeen(orderId: string, step: number): void {
    seen.value = { ...seen.value, [orderId]: step }
  }

  /** Сколько минут осталось до обещанного времени; в прошлом — ноль. */
  function etaOf(item: ApiOrderListItem): number {
    if (!item.plannedDateTime) return 0

    const left = (new Date(item.plannedDateTime).getTime() - Date.now()) / 60_000
    return left > 0 ? Math.round(left) : 0
  }

  function show(item: ApiOrderListItem, step: number, isPickup: boolean): void {
    didShow = true
    notice.value = {
      orderId: item.order.orderId,
      number: orderNumber(item.order),
      step,
      isPickup,
      eta: etaOf(item),
    }
  }

  function dismiss(): void {
    notice.value = null
  }

  /**
   * Самая поздняя доставка среди свежих заказов. Берём именно её, а не первую
   * попавшуюся: порядок списка бэкенд не обещает, а рассказать клиенту надо
   * про последний заказ, а не про тот, что оказался выше.
   *
   * Доставкой считаем только код 201: кодов завершения у бэкенда больше, чем
   * знает приложение (251–254), а `statusStep` незнакомому коду подставляет
   * последний шаг — и закрытый заказ показывался «доставленным».
   */
  function lastDelivered(items: ApiOrderListItem[]): ApiOrderListItem | undefined {
    return items
      .filter((item) => item.statusCode === ORDER_STATUS.delivered)
      .sort((a, b) => momentOf(b) - momentOf(a))[0]
  }

  let timer: ReturnType<typeof setTimeout> | undefined

  /**
   * Сверка заказов. Карточка одна и всегда про самый свежий заказ: если
   * клиент вернулся в приложение через час, ему важен нынешний статус,
   * а не вся цепочка задним числом.
   *
   * По истории не ходим: раньше цикл брал первый подходящий заказ сверху и,
   * пометив его показанным, на следующем запуске доставал следующий — клиент
   * каждый раз получал карточку про новый старый заказ. Теперь кандидат ровно
   * один: едущий заказ, а если такого нет — последняя доставка.
   *
   * Про отмену и прочие коды завершения не рассказываем: поздравлять нечем,
   * а что они означают, бэкенд приложению не объясняет.
   */
  async function sync(): Promise<void> {
    clearTimeout(timer)
    /* Свою карточку клиент в этот запуск уже видел — больше его не трогаем */
    if (didShow || !userStore.userId) return

    const list = (await request.execute(userStore.userId)) ?? []
    /* История приходит целиком — старое не показываем и опрос из-за него не заводим */
    const fresh = list.filter(isFresh)

    /* Едущий заказ важнее: о доставке рассказываем, только когда везти нечего */
    const active = fresh.find((item) => isActiveOrder(item.statusCode))
    const candidate = active ?? lastDelivered(fresh)

    if (candidate) {
      const isPickup = candidate.order.orderTypeId === ORDER_TYPE.pickup
      const step = statusStep(candidate.statusCode, isPickup)
      const id = candidate.order.orderId
      const isDone = !isActiveOrder(candidate.statusCode)

      /* Про доставку — ровно один раз за всё время, отметка переживает перезапуск */
      if (!isDone || step > (seen.value[id] ?? -1)) {
        if (isDone) markSeen(id, step)
        show(candidate, step, isPickup)
      }
    }

    /* Ждать нового статуса имеет смысл, только пока карточку не показали */
    if (active && !didShow) timer = setTimeout(() => void sync(), POLL)
  }

  /**
   * Наблюдение включает главный экран: карточка показывается только там,
   * поэтому и опрашивать заказы имеет смысл, только пока клиент на главной.
   *
   * Отметку о показе не сбрасываем: сюда клиент попадает и с запуска, и с
   * любого возврата из корзины или меню, а карточка ему нужна один раз.
   */
  function watchOrders(): void {
    void sync()
  }

  /** Ушли с главной — гасим и опрос, и карточку: показывать её негде. */
  function stop(): void {
    clearTimeout(timer)
    notice.value = null
  }

  return { notice, sync, watchOrders, stop, dismiss }
})
